import { ReactElement } from "react";
import Tippy, { type TippyProps } from "@tippyjs/react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import "./tooltip_tippy.css";

/**
 * 説明文の共通タイポグラフィ。デザインシステムの `bodytext-11` に揃える
 * （tooltip_tippy.css 側はフォント・サイズは同一だが line-height: 1 のため、
 * ここで行間を上書きする）。改行（\n）は意図どおり反映し、長文は読みやすい
 * 幅で折り返す。
 */
const Description = styled.span.attrs({ className: "bodytext-11" })`
  display: block;
  white-space: pre-line;
  max-width: 340px;
`;

/**
 * 要素自身または子孫のいずれかが横方向に見切れている（ellipsis 等で全文が
 * 表示できていない）かを判定する。インライン要素は clientWidth/scrollWidth が
 * 0 になるため誤判定しない。1px の誤差はサブピクセルレンダリング対策。
 */
const hasTruncatedContent = (el: HTMLElement): boolean => {
  if (el.scrollWidth > el.clientWidth + 1) return true;
  for (const child of Array.from(el.children)) {
    if (child instanceof HTMLElement && hasTruncatedContent(child)) return true;
  }
  return false;
};

interface ToolTipProps {
  description: string;
  placement?: "top" | "bottom" | "left" | "right" | "top-start" | "top-end" | "bottom-start" | "bottom-end" | "left-start" | "left-end" | "right-start" | "right-end";
  children: ReactElement;
  offset?: [number, number];
  /** Tooltip の描画先。デフォルトは body。ポータル/モーダル内では "parent" を推奨 */
  appendTo?: "parent" | Element | (() => Element);
  /** Tippy テーマ名（tooltip_tippy.css で定義） */
  theme?: string;
  /** プログラム制御。undefined = ホバーで自動制御、true/false = 強制表示/非表示 */
  visible?: boolean;
  /**
   * true のとき、ラップした要素（またはその子孫）が ellipsis 等で見切れている
   * 場合のみツールチップを表示する。「セルの全文をホバーで見せる」用途で、
   * 見切れていないセルに表示と同じ内容が重複表示されるのを防ぐ。
   */
  truncatedOnly?: boolean;
  /**
   * Popper の挙動を上書きする。スクロール領域内・大量要素での位置ズレ対策に
   * `strategy: "fixed"` や `computeStyles.adaptive: false` を渡す用途で使う。
   */
  popperOptions?: TippyProps["popperOptions"];
}

const ToolTip = ({ description, placement = "top", children, offset = [0, 0], appendTo, theme, visible, truncatedOnly, popperOptions }: ToolTipProps) => {
  const isMobile = useSelector((state: any) => state.device.isMobile);

  return (
    <Tippy
      content={<Description>{description}</Description>}
      arrow={false}
      delay={[0, 0]}
      duration={[0, 300]}
      placement={placement}
      offset={offset}
      appendTo={appendTo ?? (() => document.body)}
      disabled={!description || isMobile}
      {...(theme ? { theme } : {})}
      {...(visible !== undefined ? { visible } : {})}
      {...(truncatedOnly
        ? {
            onShow: (instance: { reference: Element }) =>
              hasTruncatedContent(instance.reference as HTMLElement)
                ? undefined
                : false,
          }
        : {})}
      {...(popperOptions ? { popperOptions } : {})}
    >
      {children}
    </Tippy>
  );
};

ToolTip.displayName = 'ToolTip';

export default ToolTip; 